import { AngleRightLightIcon } from '@mm/icons';
import { useLeadAssignmentsCRUDActions } from '@mm/state/actions/LeadAssignmentsCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { selectCurrentUser } from '@mm/state/selectors/userSelector';
import {
  activeChatbotIdSelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import noProfilePicIcon from 'assets/icons/contact_noimage.svg';
import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import useRouter from 'use-react-router';
import { SettingsRoutes } from '../../../../../../routes/settings-routes';
import './AgentAvailability.scss';
import { AwayUntilDateSelector } from './AwayUntilDateSelector/AwayUntilDateSelector';

const awayPresets = [
  {
    name: '1 hour',
    value: () => moment().add(1, 'hour').toISOString(),
  },
  {
    name: '3 hour',
    value: () => moment().add(3, 'hour').toISOString(),
  },
  {
    name: 'Today',
    value: () => moment().endOf('day').toISOString(),
  },
];

const upatePagination = {
  pageNumber: 1,
  pageSize: 50,
};
export const AgentAvailability = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isAwayDropdownOpen, setIsAwayDropdownOpen] = useState(false);
  const [isAwayUntilModalOpen, setIsAwayUntilModalOpen] = useState(false);

  const currentUser = useSelector(selectCurrentUser);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const companyId = useSelector(selectActiveCompanyId);
  const daysForIndefinitelyAway = useSelector(
    (store: StoreState) => store.env.daysForIndefinitelyAway,
  );

  const { update } = useUsersCRUDActions();
  const { retrieveAll: retrieveAllLeadAssignments } =
    useLeadAssignmentsCRUDActions();
  const { history } = useRouter();

  useEffect(() => {
    retrieveAllLeadAssignments(chatbotId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!currentUser) {
    return null;
  }

  const updateAgentIsAvailable = () => {
    setIsDropdownOpen(false);
    update(
      companyId,
      { ...currentUser, away: false, awayUntil: null },
      upatePagination,
    );
  };
  const onDropdownToggle = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const onStatusDropdownToggle = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsStatusDropdownOpen(!isStatusDropdownOpen);
  };

  const onAwayDropdownToggle = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAwayDropdownOpen(!isAwayDropdownOpen);
  };

  const agentStatusClassName = classNames(`agent-availability__status-label`, {
    'agent-availability__status-label--away': currentUser.away,
  });

  const statusMenuClassNames = classNames(
    'agent-availability__status-option-label',
    {
      'agent-availability__status-option-label--away': currentUser.away,
    },
  );

  return (
    <>
      {isAwayUntilModalOpen && (
        <AwayUntilDateSelector
          closeModal={() => setIsAwayUntilModalOpen(false)}
          currentUser={currentUser}
        />
      )}
      <div className="agent-availability">
        <Dropdown
          direction="down"
          isOpen={isDropdownOpen}
          toggle={onDropdownToggle}
        >
          <DropdownToggle>
            <div className="agent-availability__avatar-component">
              <img
                src={currentUser.image || noProfilePicIcon}
                alt="Your avatar"
                className="agent-availability__avatar"
              />
              <div className={agentStatusClassName} />
            </div>
          </DropdownToggle>
          <DropdownMenu className="agent-availability__main-dropdown-menu">
            <DropdownItem disabled className="agent-availability__agent-info">
              <img
                src={currentUser.image || noProfilePicIcon}
                alt="Your avatar"
                className="agent-availability__avatar"
              />
              <div className="agent-availability__agent-name">
                {currentUser.personaName ||
                  `${currentUser.firstName} ${currentUser.lastName}`}
              </div>
            </DropdownItem>
            <div className="agent-availability__away-status-dropdown">
              <Dropdown
                direction="right"
                isOpen={isStatusDropdownOpen}
                toggle={onStatusDropdownToggle}
              >
                <DropdownToggle className="agent-availability__status-option">
                  <div className={statusMenuClassNames} />
                  {currentUser.away ? 'Away' : 'Available'}

                  <span className="agent-availability__choose-data-option">
                    Change
                  </span>

                  <AngleRightLightIcon className="agent-availability__dropdown-item-expand-icon" />
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem
                    onClick={updateAgentIsAvailable}
                    className="agent-availability__status-option"
                  >
                    <div className="agent-availability__status-option-label" />
                    Available
                  </DropdownItem>
                  <div className="agent-availability__away-status-dropdown">
                    <Dropdown
                      direction="right"
                      isOpen={isAwayDropdownOpen}
                      toggle={onAwayDropdownToggle}
                    >
                      <DropdownToggle className="agent-availability__status-option">
                        <div className="agent-availability__status-option-label agent-availability__status-option-label--away" />
                        <div className="agent-availability__dropdown-item-caption">
                          Away
                        </div>
                        <AngleRightLightIcon className="agent-availability__dropdown-item-expand-icon" />
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem
                          onClick={() => {
                            setIsDropdownOpen(false);
                            update(
                              companyId,
                              {
                                ...currentUser,
                                awayUntil: moment()
                                  .add(daysForIndefinitelyAway, 'days')
                                  .toISOString(),
                              },

                              upatePagination,
                            );
                          }}
                        >
                          Indefinitely
                        </DropdownItem>
                        {awayPresets.map(preset => (
                          <DropdownItem
                            key={preset.name}
                            onClick={() => {
                              setIsDropdownOpen(false);
                              update(
                                companyId,
                                {
                                  ...currentUser,
                                  awayUntil: preset.value(),
                                },

                                upatePagination,
                              );
                            }}
                          >
                            {preset.name}
                          </DropdownItem>
                        ))}
                        <DropdownItem
                          onClick={() => {
                            setIsDropdownOpen(false);
                            setIsAwayUntilModalOpen(true);
                          }}
                        >
                          Until.
                          <span className="agent-availability__choose-data-option">
                            Choose date
                          </span>
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </DropdownMenu>
              </Dropdown>
            </div>
            <DropdownItem
              onClick={() => history.push(SettingsRoutes.myAccount.getLink())}
            >
              Agent Persona Settings
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
    </>
  );
};
